// eslint-disable-import/no-named-as-default

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  USER_ROLES,
  Constants,
  NavigationNodeId,
} from '@pwc/pwc-deals-web-common';
import {
  getFakeClientUser,
  getUserRole,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';

import { SyncedInfoProvider } from 'common/components/Autosync/SyncedInfoProvider.tsx';
import { Button, Icon } from '@pwc/pwc-deals-ui';
import { getNavigationTitle } from 'common/helpers/getCustomNavigationTitles';
import DealDetails from '../../../common/components/DealDetails';
import Separator from '../../../common/components/Separator';
import UserSnippet from '../../../common/components/UserSnippet';
import featureFlags from '../../../common/helpers/featureFlags';
import {
  useProjectsStatuses,
  getProjectStatus,
} from '../../../common/hooks/useProjectsStatuses';
import { getUserViewStatusCountsForProject } from '../../../common/services/projectService';
import {
  addAnalysisTypePath,
  createHypothesisPath,
} from '../../../common/services/urlService';
import { getNarrativesCount } from '../../narratives/narrativesService';
import ProjectStatePill from '../../projectStatePill/ProjectStatePill';
import ShowForRole from '../../security/ShowForRole';
import NewAndUpdated from '../common/NewAndUpdated.tsx';
import { getUserByRole } from '../common/ProjectModel';
import { fetchUserViewCountsSucceeded, getProject } from '../projectSlice';
import ContentLinkSubPanel from './ContentLinkSubPanel';
import KPIContainer from './KPIs/KPIContainer';
import ProjectOverviewModuleContainer from './ProjectOverview/ProjectOverviewModuleContainer';
import DiagnosticContainer from './Diagnostic/DiagnosticContainer';
import { isProjectReadOnly } from '../../../common/helpers/projectStatusesUtils';
import '../../projects/ProjectList.scss';
import './ProjectOverview.scss';
import Accordion from '../../../common/components/Accordion/AccordionOnlyMobile';
import useIsMountedRef from '../../../common/hooks/useIsMountedRef';
import useVersionToggle from '../../../common/hooks/useVersionToggle';
import {
  entitySharedStatusUpdateSuccess,
  getCommentPanelOpened,
  getViewingStatus,
  setCommentPanelOpened,
  viewUnresolvedComments,
} from '../../comments/commentsSlice';
import VersionToggleContainer from '../../../common/components/VersionToggle/VersionToggleContainer';
import { resetIsSaving } from '../../../common/redux/project-overview/projectOverviewSlice';
import { getIsDarkModeSelector } from '../../../common/redux/userSettings/userSettingsSlice';
import { COMMENT_VIEWING_STATUS } from '../../comments/constants';
import { getKPIs } from '../KPIService';

const ProjectOverview = () => {
  const { t } = useTranslation();
  const KFATitle = getNavigationTitle(NavigationNodeId.KEY_FOCUS_AREAS);

  const isMountedRef = useIsMountedRef();
  const dispatch = useDispatch();
  const project = useSelector(getProject);
  const viewingStatus = useSelector(getViewingStatus);
  const commentPanelOpened = useSelector(getCommentPanelOpened);
  const darkMode = useSelector(getIsDarkModeSelector);
  const engagementLead = getUserByRole(
    project,
    Constants.USER_ROLES.ENGAGEMENT_LEAD
  );
  const projectsStates = useProjectsStatuses();
  const projectStatus = getProjectStatus(projectsStates, project.id);

  const engagementManager = getUserByRole(
    project,
    Constants.USER_ROLES.ENGAGEMENT_MANAGER
  );
  const addHypothesisTypeHref = createHypothesisPath(project.id);
  const addAnalysisTypeHref = addAnalysisTypePath(project.id);

  const [counts, setCounts] = useState(false);
  const [kpis, setKpis] = useState([]);
  const [kpisLoaded, setKpisLoaded] = useState(false);

  const { isEntitySharedWithClient, commentCounts } = useVersionToggle(
    isMountedRef.current
  );

  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const isFakeClient = useSelector(getFakeClientUser);

  const showClientVersion = useCallback(() => {
    dispatch(entitySharedStatusUpdateSuccess(true));
  }, [dispatch]);

  const showDraftVersion = useCallback(() => {
    dispatch(entitySharedStatusUpdateSuccess(false));
  }, [dispatch]);

  const projectSourceName = useMemo(() => {
    if (!project.ProjectInitiationName) return null;
    const sourceName = project.ProjectInitiationName.trim().toLowerCase();
    const currentName = project.name.trim().toLowerCase();

    if (sourceName === currentName) return null;

    return project.ProjectInitiationName;
  }, [project.name, project.ProjectInitiationName]);

  useEffect(() => {
    return () => {
      dispatch(resetIsSaving());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFakeClient) {
      showClientVersion();
    }
  }, [dispatch, isFakeClient, showClientVersion]);

  useEffect(() => {
    const fetch = async () => {
      if (isMountedRef.current && project.id) {
        if (!featureFlags.isDisabledCountApi) {
          const userViewCountsResponse =
            await getUserViewStatusCountsForProject(project.id);
          dispatch(fetchUserViewCountsSucceeded(userViewCountsResponse));
        }

        const narrativeCounts = await getNarrativesCount(project.id);
        if (narrativeCounts) {
          setCounts(narrativeCounts);
        }
      }
    };

    fetch();
    const timerId = setInterval(fetch, 60000);

    return () => {
      clearInterval(timerId);
    };
  }, [project.id, dispatch, isMountedRef]);

  useEffect(() => {
    /*  We will load KPIs from Project API and prevent the Welcome Box check and Project Topic load
        from happening until it has returned.  This is to fix the concurrency issue in bug 208519.
        This is the first call to Project API, so it will run EF migrations as needed. */
    let isMounted = true;
    if (project.id) {
      setKpisLoaded(false);
      (async () => {
        const response = await getKPIs(project.id);
        if (isMounted) {
          setKpis(response);
          setKpisLoaded(true);
        }
      })();
    }

    return () => {
      isMounted = false;
      setKpis([]);
      setKpisLoaded(false);
    };
  }, [project.id]);

  if (!project.id) {
    return null;
  }

  const handlePanelButtonClick = () => {
    if (viewingStatus !== COMMENT_VIEWING_STATUS.UNRESOLVED) {
      dispatch(viewUnresolvedComments());
    }
    dispatch(setCommentPanelOpened(!commentPanelOpened));
  };

  const isSectionVisible = (id) => {
    if (isPwcUser) {
      return true;
    }

    const item = project.navigation?.find((nav) => nav.id === id);

    return item ? !item.isHidden : true;
  };

  return (
    <SyncedInfoProvider value={{ shareWithAll: isEntitySharedWithClient }}>
      <div
        className={classNames(
          'project-overview__header',
          !isPwcUser ? 'current-user-is-client' : 'current-user-is-pwc'
        )}
      >
        <VersionToggleContainer
          mode="view"
          isEntitySharedWithClient={isEntitySharedWithClient}
          projectId={project.id}
          config={{
            commentCounts,
            draftVersion: {
              versionNumber: -1,
            },
            sharedVersion: {
              versionNumber: -1,
            },
            draftOnClick: showDraftVersion,
            sharedOnClick: showClientVersion,
          }}
        />
        <Button
          className="mobile-panel-button"
          aria-label="mobile-panel-button icon"
          onClick={handlePanelButtonClick}
          data-testid="panel-button"
        >
          {darkMode ? (
            <Icon name="tooltip-open-darkMode" width="34" height="32" />
          ) : (
            <Icon name="tooltip-open-lightMode" width="34" height="32" />
          )}
        </Button>
      </div>
      <div className="project-overview">
        <h2>Project Overview</h2>
        <div className="project-container content-container">
          <div
            key={project.id}
            className={classNames('project-panel', {
              'project-panel__readonly': isProjectReadOnly(projectStatus),
            })}
          >
            <div className="project-panel-row">
              <div className="project-panel-col project-title-description-container">
                <span className="project-title" data-testid="project-title">
                  <span className="title-overflow">{project.name}</span>
                  <ProjectStatePill
                    pillText={t('projectInitiating')}
                    projectId={project.id}
                    showWhenActive={false}
                    isSmall
                  />
                </span>
              </div>
            </div>
            {projectSourceName && isPwcUser && (
              <div className="project-source-name">({projectSourceName})</div>
            )}
            <div className="project-description-container">
              <div
                className="project-description"
                data-testid="project-description"
              >
                {project.description || ' '}
              </div>
              {!featureFlags.isDisabledCountApi && (
                <NewAndUpdated
                  newContent={project.newCount || 0}
                  updatedContent={project.updatedCount || 0}
                  projectId={project.id}
                  shouldRetrieveNotificationCount
                />
              )}
            </div>
            <ShowForRole
              projectId={project.id}
              roles={Constants.USER_ROLE_GROUPS.INTERNAL}
            >
              <Separator />
              <Accordion title="Info">
                <div className="project-panel-columns">
                  <div className="user-details-container">
                    <div>
                      <UserSnippet
                        dataTestId={`project-${project.id}-engagement-lead`}
                        firstName={engagementLead.firstName}
                        lastName={engagementLead.lastName}
                        position="Engagement Lead"
                      />
                    </div>
                    <div>
                      <UserSnippet
                        dataTestId={`project-${project.id}-engagement-manager`}
                        firstName={engagementManager.firstName}
                        lastName={engagementManager.lastName}
                        position="Engagement Manager"
                      />
                    </div>
                  </div>
                  <div className="deal-details-container">
                    <div className="detail-section">
                      <DealDetails
                        dataTestId={`project-${project.id}-client-details`}
                        title={t('client')}
                        content={project.clientName}
                      />
                    </div>
                    <div className="detail-section">
                      <DealDetails
                        dataTestId={`project-${project.id}-target-details`}
                        title={t('target')}
                        content={project.target}
                      />
                    </div>
                    <div className="detail-section">
                      <DealDetails
                        dataTestId={`project-${project.id}-industry-status`}
                        title={t('industry')}
                        content={project.industry}
                      />
                    </div>
                  </div>
                </div>
              </Accordion>
            </ShowForRole>
          </div>

          {kpisLoaded && (
            <DiagnosticContainer
              id={project.id}
              type={project.type}
              showDiagnostic={project.showDiagnostic}
            />
          )}
          <KPIContainer kpis={kpis} />
          {kpisLoaded && (
            <ProjectOverviewModuleContainer
              sharedTopics={isEntitySharedWithClient}
            />
          )}

          {counts && (
            <div
              className="project-overview-subpanel-container"
              data-testid="projectOverviewSubpanelContainer"
            >
              {counts.kfas >= 0 &&
                isSectionVisible(NavigationNodeId.KEY_FOCUS_AREAS) && (
                  <ContentLinkSubPanel
                    itemCount={counts.kfas}
                    contentAddLinkTo={addHypothesisTypeHref}
                    contentListIconName="hypothesis"
                    contentListLinkTo={`/projects/${project.id}/key-focus-areas`}
                    contentType={KFATitle}
                  />
                )}
              {counts.analyses >= 0 &&
                isSectionVisible(NavigationNodeId.ANALYSES) && (
                  <ContentLinkSubPanel
                    itemCount={counts.analyses}
                    contentAddLinkTo={addAnalysisTypeHref}
                    contentListIconName="analysis"
                    contentListLinkTo={`/projects/${project.id}/analyses${
                      featureFlags.freeForm2Enabled ? '2' : ''
                    }`}
                    contentType="Analyses"
                  />
                )}
            </div>
          )}
        </div>
      </div>
    </SyncedInfoProvider>
  );
};
export default ProjectOverview;
